import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Pagination (re-export pattern)
// ---------------------------------------------------------------------------

export interface EnterprisePagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

// ---------------------------------------------------------------------------
// GDPR
// ---------------------------------------------------------------------------

export type GDPRRequestType = "export" | "delete" | "rectification" | "access";
export type GDPRRequestStatus = "pending" | "processing" | "completed" | "rejected";

export interface GDPRRequest {
  readonly id: string;
  readonly type: GDPRRequestType;
  readonly subject_email: string;
  readonly subject_name: string;
  readonly status: GDPRRequestStatus;
  readonly submitted_at: string;
  readonly completed_at: string | null;
  readonly handled_by: string | null;
  readonly notes: string | null;
}

export interface GDPRListResponse {
  readonly requests: readonly GDPRRequest[];
  readonly pagination: EnterprisePagination;
}

export interface GDPRListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly type?: GDPRRequestType | null;
  readonly status?: GDPRRequestStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreateGDPRRequestInput {
  readonly type: GDPRRequestType;
  readonly subject_email: string;
  readonly subject_name: string;
  readonly notes?: string;
}

// ---------------------------------------------------------------------------
// E-Invoice (NINEA / DGI Senegal)
// ---------------------------------------------------------------------------

export type EInvoiceStatus = "pending" | "submitted" | "accepted" | "rejected";

export interface EInvoice {
  readonly id: string;
  readonly invoice_number: string;
  readonly order_ref: string;
  readonly customer_name: string;
  readonly amount_ht_fcfa: number;
  readonly tva_fcfa: number;
  readonly amount_ttc_fcfa: number;
  readonly ninea: string | null;
  readonly status: EInvoiceStatus;
  readonly submitted_at: string | null;
  readonly dgi_ref: string | null;
}

export interface EInvoiceListResponse {
  readonly invoices: readonly EInvoice[];
  readonly pagination: EnterprisePagination;
}

export interface EInvoiceListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: EInvoiceStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

// ---------------------------------------------------------------------------
// Backup
// ---------------------------------------------------------------------------

export type BackupJobType = "full" | "incremental" | "schema_only";
export type BackupJobStatus = "scheduled" | "running" | "completed" | "failed";

export interface BackupJob {
  readonly id: string;
  readonly type: BackupJobType;
  readonly status: BackupJobStatus;
  readonly size_mb: number | null;
  readonly created_at: string;
  readonly completed_at: string | null;
  readonly download_url: string | null;
  readonly error: string | null;
  readonly retention_days: number;
}

export interface BackupSchedule {
  readonly frequency: "daily" | "weekly" | "monthly";
  readonly time: string;
  readonly retention_days: number;
  readonly enabled: boolean;
}

export interface BackupResponse {
  readonly jobs: readonly BackupJob[];
  readonly schedule: BackupSchedule;
  readonly pagination: EnterprisePagination;
}

export interface UpdateBackupScheduleInput {
  readonly frequency?: "daily" | "weekly" | "monthly";
  readonly time?: string;
  readonly retention_days?: number;
  readonly enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Enterprise API Keys
// ---------------------------------------------------------------------------

export interface EnterpriseApiKey {
  readonly id: string;
  readonly name: string;
  readonly prefix: string;
  readonly scopes: readonly string[];
  readonly created_at: string;
  readonly last_used_at: string | null;
  readonly expires_at: string | null;
  readonly is_active: boolean;
  readonly created_by: string;
}

export interface ApiKeyListResponse {
  readonly keys: readonly EnterpriseApiKey[];
  readonly pagination: EnterprisePagination;
}

export interface CreateApiKeyInput {
  readonly name: string;
  readonly scopes: readonly string[];
  readonly expires_at?: string;
}

export interface CreateApiKeyResponse {
  readonly key: string;
  readonly apiKey: EnterpriseApiKey;
}

// ---------------------------------------------------------------------------
// Enterprise Webhooks
// ---------------------------------------------------------------------------

export interface EnterpriseWebhook {
  readonly id: string;
  readonly url: string;
  readonly events: readonly string[];
  readonly is_active: boolean;
  readonly created_at: string;
  readonly last_triggered_at: string | null;
  readonly failure_count: number;
  readonly secret: string | null;
}

export interface WebhookDelivery {
  readonly id: string;
  readonly webhook_id: string;
  readonly event: string;
  readonly status_code: number;
  readonly success: boolean;
  readonly triggered_at: string;
  readonly response_time_ms: number;
}

export interface WebhookListResponse {
  readonly webhooks: readonly EnterpriseWebhook[];
  readonly pagination: EnterprisePagination;
}

export interface WebhookDeliveryListResponse {
  readonly deliveries: readonly WebhookDelivery[];
  readonly pagination: EnterprisePagination;
}

export interface CreateWebhookInput {
  readonly url: string;
  readonly events: readonly string[];
  readonly secret?: string;
}

// ---------------------------------------------------------------------------
// System Health
// ---------------------------------------------------------------------------

export type ServiceStatusValue = "operational" | "degraded" | "down";
export type OverallStatusValue = "operational" | "degraded" | "incident";

export interface ServiceStatus {
  readonly name: string;
  readonly status: ServiceStatusValue;
  readonly response_time_ms: number;
  readonly uptime_percent: number;
  readonly last_checked_at: string;
}

export interface SystemHealthResponse {
  readonly overall: OverallStatusValue;
  readonly services: readonly ServiceStatus[];
  readonly uptime_30d_percent: number;
}

// ---------------------------------------------------------------------------
// Performance Monitor
// ---------------------------------------------------------------------------

export type MetricStatus = "ok" | "warning" | "critical";

export interface PerformanceMetric {
  readonly name: string;
  readonly value: number;
  readonly unit: string;
  readonly threshold: number;
  readonly status: MetricStatus;
}

export interface ApiEndpointStats {
  readonly path: string;
  readonly method: string;
  readonly avg_response_ms: number;
  readonly p95_ms: number;
  readonly error_rate_percent: number;
  readonly calls_per_hour: number;
}

export interface PerformanceResponse {
  readonly metrics: readonly PerformanceMetric[];
  readonly top_endpoints: readonly ApiEndpointStats[];
  readonly slow_endpoints: readonly ApiEndpointStats[];
}

// ---------------------------------------------------------------------------
// Feature Flags
// ---------------------------------------------------------------------------

export type FlagEnvironment = "production" | "staging" | "all";

export interface FeatureFlag {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly rollout_percent: number;
  readonly environment: FlagEnvironment;
  readonly updated_at: string;
  readonly updated_by: string;
}

export interface FeatureFlagListResponse {
  readonly flags: readonly FeatureFlag[];
}

export interface UpdateFeatureFlagInput {
  readonly enabled?: boolean;
  readonly rollout_percent?: number;
  readonly environment?: FlagEnvironment;
}

// ---------------------------------------------------------------------------
// Data Export
// ---------------------------------------------------------------------------

export type ExportFormat = "csv" | "json" | "xlsx";
export type ExportStatus = "pending" | "processing" | "ready" | "failed";

export interface DataExportJob {
  readonly id: string;
  readonly name: string;
  readonly tables: readonly string[];
  readonly format: ExportFormat;
  readonly status: ExportStatus;
  readonly row_count: number | null;
  readonly file_url: string | null;
  readonly created_at: string;
  readonly completed_at: string | null;
  readonly expires_at: string;
}

export interface DataExportListResponse {
  readonly jobs: readonly DataExportJob[];
  readonly pagination: EnterprisePagination;
}

export interface CreateExportInput {
  readonly name: string;
  readonly tables: readonly string[];
  readonly format: ExportFormat;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const enterpriseApi = {
  gdpr: {
    list: (params: GDPRListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        type: params.type,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<GDPRListResponse>(`/api/admin/enterprise/gdpr${qs}`);
    },

    create: (input: CreateGDPRRequestInput) =>
      adminFetch<{ readonly request: GDPRRequest }>("/api/admin/enterprise/gdpr", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    process: (id: string) =>
      adminFetch<{ readonly request: GDPRRequest }>(
        `/api/admin/enterprise/gdpr/${encodeURIComponent(id)}/process`,
        { method: "PATCH" },
      ),

    complete: (id: string) =>
      adminFetch<{ readonly request: GDPRRequest }>(
        `/api/admin/enterprise/gdpr/${encodeURIComponent(id)}/complete`,
        { method: "PATCH" },
      ),

    reject: (id: string) =>
      adminFetch<{ readonly request: GDPRRequest }>(
        `/api/admin/enterprise/gdpr/${encodeURIComponent(id)}/reject`,
        { method: "PATCH" },
      ),
  },

  einvoice: {
    list: (params: EInvoiceListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<EInvoiceListResponse>(`/api/admin/enterprise/einvoice${qs}`);
    },

    submit: (id: string) =>
      adminFetch<{ readonly invoice: EInvoice }>(
        `/api/admin/enterprise/einvoice/${encodeURIComponent(id)}/submit`,
        { method: "POST" },
      ),
  },

  backup: {
    list: () => adminFetch<BackupResponse>("/api/admin/enterprise/backup"),

    trigger: (type: BackupJobType) =>
      adminFetch<{ readonly job: BackupJob }>("/api/admin/enterprise/backup", {
        method: "POST",
        body: JSON.stringify({ type }),
      }),

    updateSchedule: (input: UpdateBackupScheduleInput) =>
      adminFetch<{ readonly schedule: BackupSchedule }>(
        "/api/admin/enterprise/backup/schedule",
        { method: "PATCH", body: JSON.stringify(input) },
      ),
  },

  apiKeys: {
    list: () => adminFetch<ApiKeyListResponse>("/api/admin/enterprise/api-keys"),

    create: (input: CreateApiKeyInput) =>
      adminFetch<CreateApiKeyResponse>("/api/admin/enterprise/api-keys", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    revoke: (id: string) =>
      adminFetch<{ readonly success: boolean }>(
        `/api/admin/enterprise/api-keys/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  webhooks: {
    list: () => adminFetch<WebhookListResponse>("/api/admin/enterprise/webhooks"),

    create: (input: CreateWebhookInput) =>
      adminFetch<{ readonly webhook: EnterpriseWebhook }>(
        "/api/admin/enterprise/webhooks",
        { method: "POST", body: JSON.stringify(input) },
      ),

    delete: (id: string) =>
      adminFetch<{ readonly success: boolean }>(
        `/api/admin/enterprise/webhooks/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),

    deliveries: (id: string) =>
      adminFetch<WebhookDeliveryListResponse>(
        `/api/admin/enterprise/webhooks/${encodeURIComponent(id)}/deliveries`,
      ),
  },

  health: {
    get: () => adminFetch<SystemHealthResponse>("/api/admin/enterprise/health"),
  },

  performance: {
    get: () => adminFetch<PerformanceResponse>("/api/admin/enterprise/performance"),
  },

  flags: {
    list: () => adminFetch<FeatureFlagListResponse>("/api/admin/enterprise/feature-flags"),

    update: (id: string, input: UpdateFeatureFlagInput) =>
      adminFetch<{ readonly flag: FeatureFlag }>(
        `/api/admin/enterprise/feature-flags/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),
  },

  dataExport: {
    list: () => adminFetch<DataExportListResponse>("/api/admin/enterprise/exports"),

    create: (input: CreateExportInput) =>
      adminFetch<{ readonly job: DataExportJob }>("/api/admin/enterprise/exports", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },
} as const;
